import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";
import useFetchNews from "../HomePage/useFetchNews";
import MotionReveal from "../motion/MotionReveal";
import newsBackground from "../../assets/news_bg.svg";

function NewsLoadingPlaceholder() {
  return (
    <div className="overflow-hidden border border-[#e8edf5] bg-white shadow-sm">
      <div className="hidden min-h-[520px] grid-cols-12 gap-6 p-6 lg:grid">
        <div className="col-span-4 animate-pulse rounded bg-slate-100" />
        <div className="col-span-4 animate-pulse rounded bg-slate-100" />
        <div className="col-span-4 flex flex-col gap-5">
          <div className="h-[150px] animate-pulse rounded bg-slate-100" />
          <div className="h-[150px] animate-pulse rounded bg-slate-100" />
          <div className="h-[150px] animate-pulse rounded bg-slate-100" />
        </div>
      </div>

      <div className="hidden min-h-[360px] grid-cols-3 gap-6 p-6 md:grid lg:hidden">
        <div className="animate-pulse rounded bg-slate-100" />
        <div className="animate-pulse rounded bg-slate-100" />
        <div className="animate-pulse rounded bg-slate-100" />
      </div>

      <div className="flex min-h-[420px] flex-col items-center justify-center gap-4 px-6 py-12 text-center md:hidden">
        <div className="h-20 w-full animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-20 w-full animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-20 w-full animate-pulse rounded-2xl bg-slate-100" />
        <span className="text-sm font-medium text-slate-500">Loading...</span>
      </div>
    </div>
  );
}

export default function News() {
  const postNew = useFetchNews("tin nổi bật");
  const isLoading = postNew.length === 0;

  return (
    <section className="relative overflow-hidden py-6 md:py-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center">
        <img
          src={newsBackground}
          alt=""
          aria-hidden="true"
          className="h-[150px] mt-8 w-auto max-w-[94%] object-contain opacity-80 md:h-[210px] lg:h-[320px]"
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 2xl:max-w-[1440px]">
        <MotionReveal direction="up" delay={0.2}>
          <div className="relative !mb-16 flex flex-col items-center justify-start pt-8 text-center md:mb-6 md:pt-10">
            <div className="relative z-10">
              <div className="relative inline-flex flex-col items-center">
                <div className="relative flex items-center">
                  <h2 className="section-tech-title text-[24px] md:text-[32px] lg:text-[44px] font-bold text-[#084FA8] ">Tin nổi bật</h2>
                </div>
              </div>
              <p className="mt-4 max-w-3xl text-base font-medium leading-8 text-slate-600 md:text-[19px]">
                Cập nhật những dấu ấn nổi bật, hoạt động tiêu biểu và các thông tin đáng chú ý
                của VNPT Net trên hành trình phát triển hạ tầng số.
              </p>
            </div>
          </div>
        </MotionReveal>

        {isLoading ? (
          <MotionReveal direction="up" delay={0.25} amount={0.08}>
            <NewsLoadingPlaceholder />
          </MotionReveal>
        ) : (
          <>
            <MotionReveal direction="up" delay={0.35} amount={0.1}>
              <div className="hidden grid-cols-12 gap-6 lg:grid">
                {postNew.slice(0, 2).map((post, index) => (
                  <MotionReveal
                    key={post.id}
                    direction="up"
                    delay={0.12 + index * 0.08}
                    amount={0.1}
                    className="col-span-4 h-full"
                  >
                    <Link
                      to={`/tin-noi-bat/${post.id}`}
                      className="group flex h-full flex-col overflow-hidden shadow transition-shadow hover:shadow-md"
                    >
                      <div className="relative aspect-[16/9] w-full overflow-hidden border-b-4 border-blue-500 bg-gray-100">
                        <img
                          src={post.image}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />

                        <span className="absolute bottom-3 left-3 rounded bg-[#47D360] px-3 text-[12px] text-white">
                          Tin nổi bật
                        </span>
                      </div>

                      <div className="flex flex-1 flex-col p-4 bg-white">
                        <h3 className="line-clamp-2 text-[18px] font-semibold leading-[22px] text-gray-800 transition-colors group-hover:text-[#0072CE]">
                          {post.title}
                        </h3>

                        <p className="mt-2 flex items-center gap-1 text-gray-600">
                          <CalendarDays size={14} />
                          <span className="text-sm">{post.createDate}</span>
                        </p>

                        <p className="news-card-excerpt mt-2 flex-1 text-sm text-gray-700">
                          {post.description}
                        </p>
                      </div>
                    </Link>
                  </MotionReveal>
                ))}

                <div className="col-span-4 flex flex-col gap-5">
                  {postNew.slice(2, 5).map((post, index) => (
                    <MotionReveal
                      key={post.id}
                      direction="up"
                      delay={0.28 + index * 0.08}
                      amount={0.1}
                      className="border-b border-dashed border-gray-300 pb-5 last:border-none last:pb-0"
                    >
                      <Link to={`/tin-noi-bat/${post.id}`} className="group flex gap-4">
                        <div className="aspect-[14/9] w-[40%] overflow-hidden">
                          <img
                            src={post.image}
                            alt=""
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        </div>

                        <div className="w-[60%]">
                          <span className="rounded bg-[#47D360] px-3 py-1 text-xs text-white">
                            Tin nổi bật
                          </span>

                          <h4 className="mt-2 line-clamp-2 text-sm font-semibold transition-colors duration-200 group-hover:text-[#0072CE]">
                            {post.title}
                          </h4>

                          <p className="mt-2 flex items-center gap-1 text-gray-600">
                            <CalendarDays size={12} />
                            <span className="text-xs">{post.createDate}</span>
                          </p>
                        </div>
                      </Link>
                    </MotionReveal>
                  ))}
                </div>
              </div>
            </MotionReveal>

            <MotionReveal direction="up" delay={0.35} amount={0.1}>
              <div className="hidden grid-cols-3 gap-6 md:grid lg:hidden">
                {postNew.slice(0, 3).map((post, index) => (
                  <MotionReveal
                    key={post.id}
                    direction="up"
                    delay={0.12 + index * 0.08}
                    amount={0.1}
                  >
                    <Link
                      to={`/tin-noi-bat/${post.id}`}
                      className="group block shadow transition-shadow hover:shadow-md"
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden border-b-4 border-blue-500">
                        <img
                          src={post.image}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />

                        <span className="absolute bottom-2 left-2 rounded bg-[#47D360] px-3 py-1 text-xs text-white">
                          Tin nổi bật
                        </span>
                      </div>

                      <div className="p-3">
                        <h3 className="line-clamp-2 text-[15px] font-bold text-gray-700 group-hover:text-[#0072CE]">
                          {post.title}
                        </h3>

                        <p className="mt-2 flex items-center gap-1 text-gray-600">
                          <CalendarDays size={14} />
                          <span className="text-sm">{post.createDate}</span>
                        </p>
                      </div>
                    </Link>
                  </MotionReveal>
                ))}
              </div>
            </MotionReveal>

            <MotionReveal direction="up" delay={0.35} amount={0.1}>
              <div className="flex flex-col gap-4 md:hidden">
                {postNew.slice(0, 5).map((post, index) => (
                  <MotionReveal
                    key={post.id}
                    direction="up"
                    delay={0.12 + index * 0.08}
                    amount={0.1}
                  >
                    <div className="flex gap-4 border-b border-dashed border-gray-300 pb-4 last:border-none last:pb-0">
                      <Link
                        to={`/tin-noi-bat/${post.id}`}
                        className="group aspect-[16/9] w-1/3 overflow-hidden"
                      >
                        <img
                          src={post.image}
                          alt=""
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </Link>

                      <div className="w-[60%]">
                        <span className="rounded bg-[#47D360] px-3 py-1 text-[10px] text-white">
                          Tin nổi bật
                        </span>
                        <Link to={`/tin-noi-bat/${post.id}`}>
                          <h4 className="mt-2 line-clamp-2 text-sm font-semibold hover:text-[#0072CE]">
                            {post.title}
                          </h4>
                        </Link>
                        <p className="mt-2 flex items-center gap-1 text-gray-600">
                          <CalendarDays size={12} />
                          <span className="text-xs">{post.createDate}</span>
                        </p>
                      </div>
                    </div>
                  </MotionReveal>
                ))}
              </div>
            </MotionReveal>

            <MotionReveal direction="up" delay={0.55} amount={0.1}>
              <div className="mt-8 flex justify-center">
                <Link
                  to="/tin-noi-bat"
                  className="inline-flex items-center gap-2 rounded-full border border-[#dbe3ef] bg-white px-5 py-2.5 text-sm font-semibold text-[#0072CE] shadow-sm transition-all duration-300 hover:border-[#0072CE] hover:bg-[#f8fbff] hover:shadow"
                >
                  Xem thêm
                  <ArrowRight size={16} />
                </Link>
              </div>
            </MotionReveal>
          </>
        )}
      </div>
    </section>
  );
}
